import fp from "lodash/fp";

import { withResetState } from "store/hocs";

import { generalConstants } from "./general.constants";
import { mapApiMerchantToFrontMerchant } from "./mapping";
import {
	newErrorState,
	newRequestState,
	newSuccessState,
} from "../../common/baseReducers";
import { merchantListConstants } from "../../merchantList/merchantList.constants";

const addressDefaultValues = {
	route: "",
	apartments: "",
	locality: "",
	country: "",
	country_code: "",
	lat: "",
	lng: "",
	postal_code: "",
	administrative_area_level_1: "",
	search_address: "",
};

const initialState = {
	loading: {
		createMerchant: false,
		generalData: false,
		isMidValid: false,
		isDiscoverMidValid: false,
		isOriginalMidValid: false,
		hierarchy: false,
		formSubmit: false,
	},
	error: {
		createMerchant: "",
		generalData: "",
		isMidValid: "",
		isDiscoverMidValid: "",
		isOriginalMidValid: "",
		hierarchy: "",
	},
	isMidValid: false,
	isMidValidationPending: false,
	isOriginalMidValid: true, // True as it's an optional field
	isOriginalMidValidationPending: false,
	isDiscoverMidValid: true,
	isDiscoverMidValidationPending: false,
	generalData: {
		doYouUseAThirdPartyForFulfillment: "false",
		documentationMailingAddress: "legal_address",
		siteInspectionBy: "aurora",
		recurringPayments: "true",
		accountType: "1",
		midCanBeUpdated: true,
		processorSponsorSection: {},
		businessAddress: {
			address: addressDefaultValues,
		},
		isAuroraHouseAccount: "false",
		legalAddress: {
			isLegalAddressTheSame: "true",
			address: addressDefaultValues,
		},
		shippingAddress: {
			isShippingAddressTheSame: "true",
			address: addressDefaultValues,
		},
		primaryContactSection: {
			isPrimaryContactTheSame: "true",
		},
		ownerPrincipalSection: {
			owners: [
				{
					address: addressDefaultValues,
				},
			],
		},
		otherContactsSection: [
			{
				isMakeContactThePrimaryContact: "false",
			},
		],
		headquarters: {},
		addUnderwritingInfo: "true",
	},
	banks: [],
	billingBanks: [],
	previewLogo: null,
	salesAgents: [],
	referralPartners: [],
	hierarchy: { parent: {}, children: [], total: 0 },
};

export const getMerchantGeneralInfoState = state => state.merchantGeneral;

// prettier-ignore
const selector = property =>
	fp.flow(getMerchantGeneralInfoState, fp.get(property));

export const getSalesAgents = selector("salesAgents");

export const getReferralPartners = selector("referralPartners");

export const getMerchantNumber = selector("generalData.mid");

export const getBillingBanks = selector("billingBanks");

function merchantGeneral(state = initialState, action) {
	switch (action.type) {
		case merchantListConstants.RESET_MERCHANT_INFO_STATE:
			return {
				...initialState,
			};

		case generalConstants.MERCHANT_GET_GENERAL_DATA_REQUEST:
			return newRequestState(state, "generalData");
		case generalConstants.MERCHANT_GET_GENERAL_DATA_SUCCESS:
			const generalData = mapApiMerchantToFrontMerchant(
				action.generalData,
			);
			return newSuccessState(state, "generalData", generalData, {
				isMidValid: true,
				isMidValidationPending: false,
				isOriginalMidValid: true,
				isOriginalMidValidationPending: false,
				isDiscoverMidValid: true,
				isDiscoverMidValidationPending: false,
				isAmexSeNumberValid: true,
				isAmexSeNumberValidationPending: false,
				error: {
					...state.error,
				},
			});
		case generalConstants.MERCHANT_GET_GENERAL_DATA_ERROR:
			return newErrorState(state, "generalData", action.error);

		case generalConstants.MERCHANT_ADD_REQUEST:
			return {
				...state,
				loading: {
					...state.loading,
					formSubmit: true,
				},
			};
		case generalConstants.MERCHANT_ADD_SUCCESS:
			return {
				...state,
				loading: {
					...state.loading,
					formSubmit: false,
				},
				error: { ...state.createMerchant, createMerchant: "" },
			};
		case generalConstants.MERCHANT_ADD_ERROR:
			return {
				...state,
				loading: {
					...state.loading,
					formSubmit: false,
				},
				error: {
					...state.createMerchant,
					createMerchant: action.error,
				},
			};

		case generalConstants.MERCHANT_SET_MID_VALIDATION_PENDING: {
			return {
				...state,
				isMidValid: false,
				isMidValidationPending: true,
			};
		}
		case generalConstants.MERCHANT_VALIDATE_MID_REQUEST: {
			return newRequestState(state, "isMidValid", {
				isMidValidationPending: false,
			});
		}
		case generalConstants.MERCHANT_VALIDATE_MID_SUCCESS: {
			return newSuccessState(state, "isMidValid", action.isMidValid);
		}
		case generalConstants.MERCHANT_VALIDATE_MID_ERROR: {
			return newErrorState(state, "isMidValid", action.error);
		}

		case generalConstants.MERCHANT_SET_ORIGINAL_MID_VALIDATION_PENDING: {
			return {
				...state,
				isOriginalMidValid: false,
				isOriginalMidValidationPending: true,
			};
		}
		case generalConstants.MERCHANT_VALIDATE_ORIGINAL_MID_REQUEST: {
			return newRequestState(state, "isOriginalMidValid", {
				isOriginalMidValidationPending: false,
			});
		}
		case generalConstants.MERCHANT_VALIDATE_ORIGINAL_MID_SUCCESS: {
			return newSuccessState(
				state,
				"isOriginalMidValid",
				action.isOriginalMidValid,
			);
		}
		case generalConstants.MERCHANT_VALIDATE_ORIGINAL_MID_ERROR: {
			return newErrorState(state, "isOriginalMidValid", action.error);
		}

		case generalConstants.MERCHANT_SET_DISCOVER_MID_VALIDATION_PENDING: {
			return {
				...state,
				isDiscoverMidValid: false,
				isDiscoverMidValidationPending: true,
			};
		}
		case generalConstants.MERCHANT_VALIDATE_DISCOVER_MID_REQUEST: {
			return newRequestState(state, "isDiscoverMidValid", {
				isDiscoverMidValidationPending: false,
			});
		}
		case generalConstants.MERCHANT_VALIDATE_DISCOVER_MID_SUCCESS: {
			return newSuccessState(
				state,
				"isDiscoverMidValid",
				action.isDiscoverMidValid,
			);
		}
		case generalConstants.MERCHANT_VALIDATE_DISCOVER_MID_ERROR: {
			return newErrorState(state, "isDiscoverMidValid", action.error);
		}

		case generalConstants.MERCHANT_SET_BANKS: {
			return {
				...state,
				banks: action.banks,
			};
		}

		case generalConstants.MERCHANT_SET_BILLING_BANKS: {
			return {
				...state,
				billingBanks: action.billingBanks,
			};
		}
		case generalConstants.SET_MERCHANT_LOGO: {
			return {
				...state,
				previewLogo: action.previewLogo,
			};
		}
		case generalConstants.LOAD_SALES_AGENTS_SUCCESS: {
			return {
				...state,
				salesAgents: fp.uniqBy("id", [
					...action.salesAgents,
					...state.salesAgents,
				]),
			};
		}
		case generalConstants.LOAD_REFERRAL_PARTNERS_SUCCESS: {
			return {
				...state,
				referralPartners: fp.uniqBy("id", [
					...action.referralPartners,
					...state.referralPartners,
				]),
			};
		}

		case generalConstants.MERCHANT_GET_HIERARCHY_REQUEST: {
			return newRequestState(state, "hierarchy");
		}
		case generalConstants.MERCHANT_GET_HIERARCHY_SUCCESS: {
			return newSuccessState(state, "hierarchy", action.hierarchy);
		}
		case generalConstants.MERCHANT_SET_HIGH_RISK: {
			return {
				...state,
				generalData: {
					...state.generalData,
					isHighRisk: action.highRiskValue,
				},
			};
		}
		case generalConstants.MERCHANT_GET_HIERARCHY_ERROR: {
			return newErrorState(
				state,
				"hierarchy",
				action.error,
				initialState.hierarchy,
			);
		}

		case generalConstants.MERCHANT_UPDATE_GENERAL_DATA_REQUEST:
			return {
				...state,
				loading: {
					...state.loading,
					formSubmit: true,
				},
			};
		case generalConstants.MERCHANT_UPDATE_GENERAL_DATA_SUCCESS:
			return {
				...state,
				loading: {
					...state.loading,
					formSubmit: false,
				},
			};
		case generalConstants.MERCHANT_UPDATE_GENERAL_DATA_ERROR:
			return {
				...state,
				loading: {
					...state.loading,
					formSubmit: false,
				},
			};

		default:
			return state;
	}
}

export default withResetState({ initialState })(merchantGeneral);
